import { appCtx } from '../../ApplicationContext'
import { Injectable, Injector } from '../../injector'
import { GameType, LobbyManager } from '../../LobbyManager'
import * as FirebaseProtocol from '../FirebaseProtocol.generated'
import { LobbyType } from '../FirebaseProtocol.generated'
import { Subject } from 'rxjs'
import { FeatureToggleService } from '../../services/FeatureToggleService'
import { SitAndGoLobbyUpdate } from '../../tournaments/models'
import LobbyListData from '../../LobbyListData'

const SNG_REQUEST_PATH = '/sitandgo'
const MTT_REQUEST_PATH = '/scheduled'
const RING_GAME_REQUEST_PATH = '/cashgame'

@Injectable()
export class LobbyCommunication{
	subscriptions: string[]

	private lobbyManager = Injector.get(LobbyManager)

	constructor() {
		this.subscriptions = []
	}

	private unsubscribeFromStreams() {
		this.subscriptions.forEach((path) => {
			if (path === RING_GAME_REQUEST_PATH) {
				this.removeSubFromStreams(path, FirebaseProtocol.LobbyType.REGULAR)
			}
			if (path === SNG_REQUEST_PATH || path === MTT_REQUEST_PATH) {
				this.removeSubFromStreams(path, FirebaseProtocol.LobbyType.MTT)
			}
		})
		this.subscriptions = []
	}

	private removeSubFromStreams(path: string, type: LobbyType) {
		let unsubscribeRequest = new FirebaseProtocol.LobbyUnsubscribePacket()
		unsubscribeRequest.type = type
		unsubscribeRequest.gameid = 1
		unsubscribeRequest.address = path
		appCtx.getConnector().sendProtocolObject(unsubscribeRequest)
	}

	unsubscribe() {
		this.unsubscribeFromStreams()
		this.lobbyManager.clearLobby()
	}

	subscribeToSurge() {
		// This changes the section type and sets the data to be empty
		// the current lobby is tightly coupled with the game server and
		// empty data must be set so that the lobby tab loads.
		this.lobbyManager.beforeSectionChange(GameType.Surge)
		this.unsubscribe()
		this.lobbyManager.lobbyDataUpdated(GameType.Surge, [])
	}

	subscribeToNewPokerLobby() {
		this.unsubscribe()
		this.lobbyManager.clearLobby()
		this.subscriptions.push(RING_GAME_REQUEST_PATH, SNG_REQUEST_PATH, MTT_REQUEST_PATH)
		this.subscribe()
	}

	subscribeToCashGames() {
		this.lobbyManager.beforeSectionChange(GameType.Ring)
		LobbyListData.unsubscribeFromLobbyList(GameType.SitAndGo)
		LobbyListData.unsubscribeFromLobbyList(GameType.Tournament)
		this.unsubscribe()

		this.lobbyManager.clearLobby()
		this.subscriptions.push(`${RING_GAME_REQUEST_PATH}`)
		this.subscribe()
	}

	subscribe() {
		if (this.subscriptions.length > 0) {
			this.subscriptions.forEach((path) => {
				if (path === RING_GAME_REQUEST_PATH) {
					appCtx.getConnector().lobbySubscribe(1, path)
				}
				if (path === SNG_REQUEST_PATH || path === MTT_REQUEST_PATH) {
					this.subscribeToTournamentsWithPath(path)
				}
			})
		}
	}

	subscribeToSitAndGos() {
		this.lobbyManager.beforeSectionChange(GameType.SitAndGo)
		LobbyListData.unsubscribeFromLobbyList(GameType.Ring)
		LobbyListData.unsubscribeFromLobbyList(GameType.Tournament)
		this.unsubscribe()
		this.lobbyManager.clearLobby()
		this.subscriptions.push(SNG_REQUEST_PATH)
		this.subscribe()
	}

	subscribeToTournaments() {
		this.lobbyManager.beforeSectionChange(GameType.Tournament)
		LobbyListData.unsubscribeFromLobbyList(GameType.SitAndGo)
		LobbyListData.unsubscribeFromLobbyList(GameType.Ring)
		this.unsubscribe()
		this.lobbyManager.clearLobby()
		this.subscriptions.push(MTT_REQUEST_PATH)
		this.subscribe()
	}

	subscribeToJackpotSitAndGo() {
		this.lobbyManager.beforeSectionChange(GameType.JackpotSitAndGo)
		LobbyListData.unsubscribeFromLobbyList(GameType.SitAndGo)
		LobbyListData.unsubscribeFromLobbyList(GameType.Ring)
		this.lobbyManager.clearLobby()
		this.unsubscribe()
		// TODO: setup subscription to JSNG data

		/**
         * TECHNICAL DEBT
         * All other game types send a packet to subscribe and receive updates
         * for the list of games except for JSNG which synchronously updates.
         * This causes an issue because:
         *
         * 1) LobbyManager sets up susbcription on login
         * 2) React renders the Lobby component
         * 3) Lobby component adds a callback for changes in the lobby data
         * 4) It's never called because the JSNG game were set already
         *
         * The setTimeout here forces it to happen asynchronously, which is a
         * hack but mixing sync and async callbacks isn't good either.
         */
		setTimeout(() => {
			this.lobbyManager.lobbyDataUpdated(GameType.JackpotSitAndGo, [])
		}, 0)
	}

	subscribeToTournamentsWithPath(path) {
		var subscribeRequest = new FirebaseProtocol.LobbySubscribePacket()
		subscribeRequest.type = FirebaseProtocol.LobbyType.MTT
		subscribeRequest.gameid = 1
		subscribeRequest.address = path
		appCtx.getConnector().sendProtocolObject(subscribeRequest)
	}

	_on(classId) {
		return appCtx.getCommunicationManager().onLobbyPacket(classId)
	}

	onTableSnapshotList() {
		return this._on(FirebaseProtocol.TableSnapshotListPacket.CLASSID)
	}

	onTableUpdateList() {
		return this._on(FirebaseProtocol.TableUpdateListPacket.CLASSID)
	}

	onTableRemoved() {
		return this._on(FirebaseProtocol.TableRemovedPacket.CLASSID)
	}

	onTournamentSnapshotList() {
		return this._on(FirebaseProtocol.TournamentSnapshotListPacket.CLASSID)
	}

	onTournamentUpdates() {
		return this._on(FirebaseProtocol.TournamentUpdateListPacket.CLASSID)
	}

	onTournamentRemoved() {
		return this._on(FirebaseProtocol.TournamentRemovedPacket.CLASSID)
	}
}